import { useMemo } from 'react';

import { getFacebookLocale } from '@change-corgi/config/facebook';
import { stripHtmlTags } from '@change-corgi/core/html';
import { useI18n } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useDecisionMakerResponsePageData } from 'src/app/pages/decisionMakerResponse/details/pageContext';

import { useUpdatePhoto } from './useUpdatePhoto';

type Result = ModelHookResult<{
	pageTitle: string;
	title: string;
	description: string;
	fbLocale: string;
	photo?: {
		url: string;
		width: number;
		height: number;
	};
}>;

export function usePageMeta(): Result {
	const {
		data: {
			decisionMakerResponse: {
				message,
				decisionMaker,
				petition: { photo: petitionPhoto, originalLocale },
			},
		},
	} = useDecisionMakerResponsePageData();
	const { translate } = useI18n();
	const { resolveUrl } = useUtilityContext();

	const updatePhoto = useUpdatePhoto();

	return {
		data: {
			pageTitle: translate('fe.pages.decision_maker_response.title'),
			title: decisionMaker.displayName,
			description: stripHtmlTags(message).substring(0, 400),
			fbLocale: getFacebookLocale(originalLocale.localeCode) || getFacebookLocale('en-US'),
			photo: useMemo(() => {
				if (updatePhoto) return updatePhoto;

				if (petitionPhoto?.petitionLarge) {
					return {
						url: resolveUrl(petitionPhoto.petitionLarge.url),
						width: petitionPhoto.petitionLarge.width,
						height: petitionPhoto.petitionLarge.height,
					};
				}

				return undefined;
			}, [petitionPhoto, updatePhoto, resolveUrl]),
		},
	};
}
